import { useEffect } from 'react'
import { useReactFlow } from '@xyflow/react'
import { cloneDeep, isEqual } from 'lodash'

import { NodeSchema, Node as TNode } from 'types/transformation/types/nodes'

import { useSourcesFetched } from 'redux/hooks/SimTables'

type PruneFn<T> = (data: T) => T

export function usePruneRefs<T>(
  nodeId: string,
  prune: PruneFn<T>,
  deps: any[]
) {
  const { setNodes } = useReactFlow<TNode>()

  const sourcesFetched = useSourcesFetched()

  useEffect(() => {
    if (!sourcesFetched) return

    setNodes((nodes) => {
      let changed = false

      const next = nodes.map((n) => {
        if (n.id !== nodeId) return n

        const newData = prune(cloneDeep(n.data) as T)

        // Skip the write when pruning changed nothing. The node's own data is
        // part of its input-schema subscription, so an unconditional rewrite
        // retriggers this effect and loops forever — React 18 spun silently,
        // React 19 flushes the cascade synchronously and aborts rendering
        // with "Maximum update depth exceeded" once the graph is deep enough.
        if (isEqual(newData, n.data)) return n

        changed = true

        return { ...n, data: newData as any }
      })

      return changed ? next : nodes
    })
  }, [nodeId, setNodes, sourcesFetched, ...deps])
}

export const pruneRef = (
  ref: string | null | undefined,
  schema: NodeSchema[]
) => (ref && schema.some((s) => s.id === ref) ? ref : null)
